import type { GlobalToken } from 'antd';

/**
 * Shared helpers for AI evaluation scores (0–1 scale) shown by the recruiting modules.
 */

/**
 * Converts a 0–1 score to a whole percentage clamped to 0–100.
 * Missing or invalid scores render as 0.
 */
export function formatScore(score?: number | null): number {
  if (typeof score !== 'number' || Number.isNaN(score)) return 0;
  return Math.max(0, Math.min(100, Math.round(score * 100)));
}

/**
 * Picks the theme colour band for a 0–1 score:
 * ≥0.8 success, ≥0.6 warning, ≥0.4 orange, otherwise error.
 * Callers holding a 0–100 percentage divide by 100 first.
 */
export function getScoreColor(token: GlobalToken, score?: number | null): string {
  const pct = formatScore(score);
  if (pct >= 80) return token.colorSuccess;
  if (pct >= 60) return token.colorWarning;
  if (pct >= 40) return token.orange;
  return token.colorError;
}

/**
 * Maps an evaluation recommendation (e.g. `strong_yes`, `maybe`, `no`) to a preset colour name.
 */
export function getRecommendationColor(recommendation?: string | null): string {
  const value = (recommendation || '').toLowerCase();
  if (value.includes('yes')) return 'green';
  if (value.includes('maybe') || value.includes('weak')) return 'orange';
  if (value.includes('no')) return 'red';
  return 'blue';
}

/**
 * Maps an evaluation confidence level (`high` / `medium` / `low`) to a preset colour name.
 */
export function getConfidenceColor(confidence?: string | null): string {
  const value = (confidence || '').toLowerCase();
  if (value === 'high') return 'green';
  if (value === 'medium') return 'orange';
  return 'red';
}
